<template>
  <div class="fixed inset-0 bg-gradient-to-br from-amber-50 via-wood-50 to-amber-100 overflow-hidden flex touch-none select-none z-40">

    <!-- 紙張紋理背景 -->
    <div
      class="absolute inset-0 pointer-events-none opacity-20 mix-blend-multiply"
      :style="backgroundStyle"
    ></div>

    <!-- vis-network 畫布區域 -->
    <div
      ref="networkContainer"
      class="flex-1 relative overflow-hidden"
      style="height: 100vh;"
    ></div>

    <!-- 左上角返回按鈕 -->
    <div class="absolute top-4 left-4 z-50" @pointerdown.stop>
      <button
        @click="goBack"
        class="p-3 bg-white/95 backdrop-blur-sm border-2 border-wood-500 rounded-full text-wood-700 hover:border-wood-700 hover:text-wood-900 hover:bg-white active:scale-95 transition-all shadow-lg"
        title="返回教材頁面"
      >
        <i class="fas fa-arrow-left text-xl"></i>
      </button>
    </div>

    <!-- 右上 HUD 控制列 -->
    <div class="absolute top-4 right-4 flex flex-col gap-2 z-50" @pointerdown.stop>
      <button
        @click="resetView"
        class="p-3 bg-white/95 backdrop-blur-sm border-2 border-wood-500 rounded-full text-wood-700 hover:border-wood-700 hover:text-wood-900 active:scale-95 transition-all shadow-lg"
        title="還原視圖"
      >
        <i class="fas fa-redo text-lg"></i>
      </button>
      <button
        @click="centerView"
        class="p-3 bg-white/95 backdrop-blur-sm border-2 border-wood-500 rounded-full text-wood-700 hover:border-wood-700 active:scale-95 transition-all shadow-lg"
        title="歸位"
      >
        <i class="fas fa-crosshairs text-lg"></i>
      </button>
      <button
        @click="zoomIn"
        class="p-3 bg-white/95 backdrop-blur-sm border-2 border-wood-500 rounded-full text-wood-700 hover:bg-wood-50 active:scale-95 transition-all shadow-lg"
        title="放大"
      >
        <i class="fas fa-search-plus text-lg"></i>
      </button>
      <button
        @click="zoomOut"
        class="p-3 bg-white/95 backdrop-blur-sm border-2 border-wood-500 rounded-full text-wood-700 hover:bg-wood-50 active:scale-95 transition-all shadow-lg"
        title="縮小"
      >
        <i class="fas fa-search-minus text-lg"></i>
      </button>
      <button
        @click="togglePhysics"
        :class="[
          'p-3 bg-white/95 backdrop-blur-sm border-2 border-wood-500 rounded-full text-wood-700 hover:bg-wood-50 active:scale-95 transition-all shadow-lg',
          physicsEnabled ? 'bg-wood-100' : ''
        ]"
        title="切換物理模擬"
      >
        <i class="fas fa-atom text-lg"></i>
      </button>
    </div>

    <!-- 詳情面板 -->
    <div
      :class="[
        'absolute top-4 left-4 w-[85vw] md:w-80 bg-white/95 backdrop-blur-md border-y-[8px] border-y-wood-700 border-x border-x-wood-300',
        'shadow-2xl transition-all duration-300 ease-in-out z-[100] rounded-lg',
        getPanelAnimationClass()
      ]"
      :style="panelStyle"
      @pointerdown.stop
    >
      <button
        class="absolute top-2 right-2 p-2 cursor-pointer text-wood-600 hover:text-wood-900 active:scale-90 transition-all z-[110]"
        @click.stop="closePanel"
      >
        <i class="fas fa-times text-2xl"></i>
      </button>

      <div v-if="shouldShowPanelContent" class="p-6 relative">
        <div class="flex flex-col items-center mb-6 border-b-2 border-wood-700 pb-4">
          <div class="text-sm font-bold text-wood-700 tracking-[0.2em] mb-1">{{ currentDisplayNode.title }}</div>
          <h2 class="text-3xl font-bold text-wood-900 font-serif">{{ currentDisplayNode.label }}</h2>
        </div>

        <div class="space-y-6 font-serif">
          <div class="bg-wood-50/80 p-4 rounded-lg border border-wood-200 relative">
            <div class="absolute -top-3 left-3 bg-white px-2 text-xs font-bold text-wood-700 flex items-center gap-1">
              <i class="fas fa-feather-alt text-xs"></i> 一句話簡介
            </div>
            <div class="text-base text-wood-800 text-center font-medium leading-relaxed">
              {{ currentDisplayNode.summary }}
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="text-center p-3 border-r border-wood-200">
              <label class="text-xs text-wood-600 block mb-1">輩分</label>
              <div class="text-base text-wood-900 font-bold flex justify-center items-center gap-1">
                <i class="fas fa-map-marked-alt text-sm"></i> 第 {{ currentDisplayNode.depth + 1 }} 代
              </div>
            </div>
            <div class="text-center p-3">
              <label class="text-xs text-wood-600 block mb-1">生卒年</label>
              <div class="text-sm text-wood-900 font-bold flex justify-center items-center gap-1">
                <i class="fas fa-scroll text-sm"></i> {{ currentDisplayNode.birthDeath }}
              </div>
            </div>
          </div>

          <div class="pt-4 border-t border-wood-200 mt-2 text-center">
            <button
              class="inline-flex items-center gap-2 text-white text-sm font-bold bg-wood-700 px-5 py-2.5 rounded-full hover:bg-wood-800 transition-all shadow-lg active:scale-95 border-2 border-wood-800"
              @click="searchInSite"
            >
              <i class="fas fa-search text-sm"></i>
              <span>查詢相關貼文</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="absolute bottom-4 left-4 text-xs text-wood-700 font-serif pointer-events-none select-none bg-white/80 backdrop-blur-sm p-3 rounded-lg border border-wood-200 shadow-md">
      楊氏太極 · 傳承譜系圖<br/>
      點擊節點查看詳情 · 拖曳可移動
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import axios from 'axios'
import Papa from 'papaparse'
import { Network } from 'vis-network'
import { DataSet } from 'vis-data'

// 響應式變數
const networkContainer = ref(null)
const network = ref(null)
const nodesDataSet = ref(null)
const edgesDataSet = ref(null)
const selectedNode = ref(null)
const isPanelAnimating = ref(false)
const physicsEnabled = ref(true)
const treeData = ref(null)
const nodeMap = ref(new Map())

// 大地色系調色盤（柔和版本）
const earthColors = [
  '#B8956A', // 沙褐色 - 第一代
  '#A0826D', // 棕褐色 - 第二代
  '#C19A6B', // 駝色 - 第三代
  '#8B7355', // 深棕色 - 第四代
  '#9E7A54', // 木色 - 第五代
  '#856547', // 橄欖棕 - 第六代
  '#6D533D', // 咖啡色 - 第七代
  '#5C4433', // 深咖啡 - 第八代
]

// 根據輩分獲取顏色
const getColorByDepth = (depth) => {
  return earthColors[depth % earthColors.length]
}

// 動態產生帶有文字的 SVG 圖片（用於 circularImage）
const createAvatar = (text, bgColor, textColor = '#FFFFFF') => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100">
      <rect x="0" y="0" width="100" height="100" fill="${bgColor}" rx="50" ry="50"/>
      <text x="50" y="58" font-family="serif, Microsoft JhengHei, sans-serif" font-size="28" font-weight="bold" text-anchor="middle" fill="${textColor}">${text}</text>
    </svg>`
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
}

// 背景樣式
const backgroundStyle = computed(() => ({
  backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.5' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.4'/%3E%3C/svg%3E")`
}))

// 詳情面板樣式
const panelStyle = computed(() => ({
  backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.02) 1px, transparent 1px)',
  backgroundSize: '20px 100%'
}))

// 當前顯示的節點
const currentDisplayNode = computed(() => {
  return selectedNode.value
})

// 是否應該顯示面板內容
const shouldShowPanelContent = computed(() => {
  return selectedNode.value && !isPanelAnimating.value
})

// 獲取面板動畫類別
const getPanelAnimationClass = () => {
  if (!selectedNode.value) {
    return '-translate-x-[120%] opacity-0 pointer-events-none'
  }

  if (isPanelAnimating.value) {
    return '-translate-x-[120%] opacity-0 pointer-events-none scale-95'
  }

  return 'translate-x-0 opacity-100 pointer-events-auto scale-100'
}

// 載入 CSV 數據
const loadData = async () => {
  try {
    const response = await axios.get('/taichi-lineage20260119.csv')
    const csvData = response.data

    const parseResult = Papa.parse(csvData, {
      header: true,
      skipEmptyLines: true,
      encoding: 'UTF-8'
    })

    const treeStructure = convertCSVToTree(parseResult.data)
    treeData.value = treeStructure
    console.log('傳承譜系 CSV 數據載入成功')

    // 轉換為 vis-network 格式
    convertToVisNetwork(treeStructure)
  } catch (error) {
    console.error('無法載入傳承譜系 CSV 數據:', error)
  }
}

// 將 CSV 數據轉換為樹狀結構
const convertCSVToTree = (csvData) => {
  const nodeMapLocal = new Map()

  // 第一階段：創建所有節點
  csvData.forEach((row, index) => {
    if (!row.姓名) return

    const node = {
      id: row.序號?.toString() || `node-${index}`,
      label: row.姓名.trim(),
      title: row['流派/支系'] || '傳人',
      summary: generateSummary(row),
      birthDeath: generateBirthDeath(row),
      depth: 0,
      children: [],
      raw: row
    }

    nodeMapLocal.set(node.label, node)
    nodeMap.value.set(node.id, node)
  })

  // 第二階段：建立師承關係
  const roots = []

  csvData.forEach((row, index) => {
    if (!row.姓名) return

    const nodeName = row.姓名.trim()
    const node = nodeMapLocal.get(nodeName)

    if (!node) return

    const masterName = row.師承?.trim()

    if (masterName && masterName !== '' && masterName !== node.label) {
      const master = nodeMapLocal.get(masterName)
      if (master) {
        master.children.push(node)
        node.depth = master.depth + 1
      } else {
        if (!roots.includes(node)) {
          roots.push(node)
          node.depth = 0
        }
      }
    } else {
      if (!roots.includes(node)) {
        roots.push(node)
        node.depth = 0
      }
    }
  })

  // 第三階段：處理多個根節點
  if (roots.length === 1) {
    return roots[0]
  } else if (roots.length > 1) {
    const mainRoot = roots.find(root => root.label.includes('楊露禪')) || roots[0]
    const otherRoots = roots.filter(root => root !== mainRoot)

    otherRoots.forEach(root => {
      mainRoot.children.push(root)
      root.depth = 1
    })

    return mainRoot
  } else {
    return {
      id: 'root',
      label: '楊露禪',
      title: '創始人',
      summary: '楊氏太極拳創始人，人稱「楊無敵」。自幼在陳家溝學藝，後入京授拳，名震一時。',
      birthDeath: '1799 - 1872',
      depth: 0,
      children: []
    }
  }
}

// 生成人物簡介
const generateSummary = (row) => {
  const parts = []

  if (row.流派) {
    parts.push(`${row.流派}`)
  }

  if (row['主要活動區域']) {
    parts.push(`主要活動於${row['主要活動區域']}`)
  }

  if (row.備註) {
    parts.push(row.備註)
  }

  return parts.join('，') || '楊氏太極拳傳人'
}

// 生成生卒年字串
const generateBirthDeath = (row) => {
  const birth = row.生年
  const death = row.卒年

  if (birth && death) {
    return `${birth} - ${death}`
  } else if (birth) {
    return `${birth} - 至今`
  } else if (death) {
    return `卒於 ${death}`
  } else {
    return '年代不詳'
  }
}

// 將樹狀結構轉換為 vis-network 格式
const convertToVisNetwork = (root) => {
  const nodes = []
  const edges = []
  const visitedNodes = new Set() // 追蹤已訪問的節點

  const traverse = (node, parentId = null) => {
    // 檢查是否已訪問過此節點
    if (visitedNodes.has(node.id)) {
      console.warn(`節點 ${node.id} (${node.label}) 已存在，跳過重複添加`)
      // 如果已訪問，只需要添加邊（如果有父節點且邊不存在）
      const edgeId = `${parentId}-${node.id}`
      if (parentId !== null && !edges.find(e => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          from: parentId,
          to: node.id,
          arrows: {
            to: {
              enabled: true,
              scaleFactor: 1
            }
          },
          color: {
            color: '#8B7355',
            highlight: '#5C4033',
            opacity: 0.8
          },
          width: 2,
          smooth: {
            type: 'continuous'
          }
        })
      }
      return
    }

    // 標記為已訪問
    visitedNodes.add(node.id)

    // 創建節點
    const bgColor = getColorByDepth(node.depth)
    const nodeSize = node.depth === 0 ? 40 : (node.depth === 1 ? 35 : 30)

    nodes.push({
      id: node.id,
      label: '', // 不顯示標籤，因為名字已在圓球內
      title: `${node.title}\n${node.label}\n${node.birthDeath}`, // hover 時的提示
      shape: 'circularImage',
      image: createAvatar(node.label, bgColor, '#FFFFFF'),
      size: nodeSize,
      borderWidth: 3,
      borderWidthSelected: 5,
      color: {
        border: '#5C4033',
        background: '#FFFFFF',
        highlight: {
          border: '#3E2723',
          background: '#D4A76A'
        }
      },
      shadow: {
        enabled: true,
        color: 'rgba(92, 64, 51, 0.3)',
        size: 10,
        x: 2,
        y: 2
      },
      // 儲存完整節點資料
      nodeData: node
    })

    // 創建邊
    if (parentId !== null) {
      edges.push({
        id: `${parentId}-${node.id}`, // 確保邊的唯一性
        from: parentId,
        to: node.id,
        arrows: {
          to: {
            enabled: true,
            scaleFactor: 1
          }
        },
        color: {
          color: '#8B7355',
          highlight: '#5C4033',
          opacity: 0.8
        },
        width: 2,
        smooth: {
          type: 'continuous'
        }
      })
    }

    // 遞迴處理子節點
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => traverse(child, node.id))
    }
  }

  traverse(root)

  console.log(`轉換完成：${nodes.length} 個節點，${edges.length} 條邊`)

  // 創建 DataSet
  nodesDataSet.value = new DataSet(nodes)
  edgesDataSet.value = new DataSet(edges)

  // 初始化網路
  initNetwork()
}

// 初始化 vis-network
const initNetwork = () => {
  if (!networkContainer.value) return

  const data = {
    nodes: nodesDataSet.value,
    edges: edgesDataSet.value
  }

  const options = {
    layout: {
      randomSeed: 2 // 固定種子，讓每次重整圖形大概長一樣
    },
    physics: {
      enabled: true,
      // 使用 barnesHut 物理引擎（類似 Research Rabbit 的有機感）
      barnesHut: {
        gravitationalConstant: -3000, // 斥力：數值越小，節點推得越開
        centralGravity: 0.3,           // 向心力：把節點拉回中心
        springLength: 150,             // 彈簧長度：連線的理想長度
        springConstant: 0.04,          // 彈簧係數：越硬拉回越快
        damping: 0.09,                 // 阻尼：數值越大，晃動停止越快
        avoidOverlap: 0.5              // 避免重疊
      },
      stabilization: {
        enabled: true,
        iterations: 1000 // 初始載入時先計算 1000 次，避免一開始亂飛
      }
    },
    interaction: {
      dragNodes: true,  // 允許拖拉節點
      dragView: true,   // 允許移動畫布
      zoomView: true,   // 允許縮放
      hover: true,
      tooltipDelay: 200,
      navigationButtons: false,
      keyboard: {
        enabled: false
      }
    },
    nodes: {
      borderWidth: 3,
      borderWidthSelected: 5,
      color: {
        border: '#5C4033',
        background: '#FFFFFF',
        highlight: {
          border: '#3E2723',
          background: '#D4A76A'
        }
      },
      font: {
        color: '#3E2723',
        size: 14,
        face: 'serif, Microsoft JhengHei',
        background: 'rgba(255, 248, 220, 0.8)',
        strokeWidth: 0
      },
      shadow: {
        enabled: true,
        color: 'rgba(92, 64, 51, 0.3)',
        size: 10,
        x: 2,
        y: 2
      }
    },
    edges: {
      color: {
        color: '#8B7355',
        highlight: '#5C4033',
        hover: '#5C4033',
        opacity: 0.8
      },
      width: 2,
      selectionWidth: 4,
      arrows: {
        to: {
          enabled: true,
          scaleFactor: 1,
          type: 'arrow'
        }
      },
      smooth: {
        type: 'continuous' // 讓線條平滑
      },
      shadow: {
        enabled: true,
        color: 'rgba(92, 64, 51, 0.2)',
        size: 5,
        x: 1,
        y: 1
      }
    }
  }

  network.value = new Network(networkContainer.value, data, options)

  // 監聽節點點擊事件
  network.value.on('click', (params) => {
    if (params.nodes.length > 0) {
      const nodeId = params.nodes[0]
      const node = nodeMap.value.get(nodeId)
      if (node) {
        handleNodeClick(node)
      }
    } else {
      closePanel()
    }
  })

  // 監聽穩定化完成
  network.value.once('stabilizationIterationsDone', () => {
    console.log('網路佈局穩定化完成')
    // 保持物理引擎開啟，讓節點可以拖動並帶動其他節點
    // 如果想要固定佈局，可以取消註解下面這行
    // network.value.setOptions({ physics: false })
    // physicsEnabled.value = false
  })
}

// 處理節點點擊
const handleNodeClick = (node) => {
  if (selectedNode.value && selectedNode.value.id === node.id) {
    return
  }

  if (selectedNode.value) {
    isPanelAnimating.value = true

    setTimeout(() => {
      selectedNode.value = node

      // 高亮選中的節點
      highlightNode(node.id)

      setTimeout(() => {
        isPanelAnimating.value = false
      }, 50)
    }, 200)
  } else {
    selectedNode.value = node
    highlightNode(node.id)
  }

  // 聚焦到選中的節點
  network.value.focus(node.id, {
    scale: 1.2,
    animation: {
      duration: 500,
      easingFunction: 'easeInOutQuad'
    }
  })
}

// 高亮節點
const highlightNode = (nodeId) => {
  // 取得該節點的所有祖先
  const ancestors = getAncestors(nodeId)

  // 更新所有節點的樣式
  const allNodes = nodesDataSet.value.get()
  allNodes.forEach(node => {
    if (node.id === nodeId || ancestors.includes(node.id)) {
      nodesDataSet.value.update({
        id: node.id,
        borderWidth: 5,
        size: 30
      })
    } else {
      nodesDataSet.value.update({
        id: node.id,
        borderWidth: 3,
        size: 25
      })
    }
  })
}

// 取得節點的所有祖先
const getAncestors = (nodeId) => {
  const ancestors = []
  const edges = edgesDataSet.value.get()

  const findParent = (childId) => {
    const edge = edges.find(e => e.to === childId)
    if (edge) {
      ancestors.push(edge.from)
      findParent(edge.from)
    }
  }

  findParent(nodeId)
  return ancestors
}

// 置中視圖
const centerView = () => {
  if (network.value) {
    network.value.fit({
      animation: {
        duration: 500,
        easingFunction: 'easeInOutQuad'
      }
    })
  }
}

// 重置視圖
const resetView = () => {
  if (network.value) {
    selectedNode.value = null
    isPanelAnimating.value = false

    // 重置所有節點樣式
    const allNodes = nodesDataSet.value.get()
    allNodes.forEach(node => {
      nodesDataSet.value.update({
        id: node.id,
        borderWidth: 3,
        size: 25
      })
    })

    centerView()
  }
}

// 放大
const zoomIn = () => {
  if (network.value) {
    const scale = network.value.getScale()
    network.value.moveTo({
      scale: scale * 1.2,
      animation: {
        duration: 300,
        easingFunction: 'easeInOutQuad'
      }
    })
  }
}

// 縮小
const zoomOut = () => {
  if (network.value) {
    const scale = network.value.getScale()
    network.value.moveTo({
      scale: scale * 0.8,
      animation: {
        duration: 300,
        easingFunction: 'easeInOutQuad'
      }
    })
  }
}

// 切換物理模擬
const togglePhysics = () => {
  if (network.value) {
    physicsEnabled.value = !physicsEnabled.value
    network.value.setOptions({
      physics: {
        enabled: physicsEnabled.value
      }
    })
  }
}

// 關閉面板
const closePanel = () => {
  selectedNode.value = null
  isPanelAnimating.value = false

  // 重置所有節點樣式
  if (nodesDataSet.value) {
    const allNodes = nodesDataSet.value.get()
    allNodes.forEach(node => {
      nodesDataSet.value.update({
        id: node.id,
        borderWidth: 3,
        size: 25
      })
    })
  }
}

// 在網站內搜尋相關貼文（開啟新分頁）
const searchInSite = () => {
  if (currentDisplayNode.value) {
    const searchQuery = encodeURIComponent(currentDisplayNode.value.label)
    window.open(`/posts?q=${searchQuery}`, '_blank')
  }
}

// 返回
const goBack = () => {
  window.history.back()
}

// 組件掛載
onMounted(async () => {
  await loadData()
})

// 組件卸載
onUnmounted(() => {
  if (network.value) {
    network.value.destroy()
  }
})
</script>

<style scoped>
/* 防止文字選取 */
.select-none {
  user-select: none;
  -webkit-user-select: none;
}

/* 觸控優化 */
.touch-none {
  touch-action: none;
}

/* vis-network 自定義樣式 */
:deep(.vis-network) {
  outline: none;
}

:deep(.vis-tooltip) {
  position: absolute;
  visibility: hidden;
  padding: 8px 12px;
  white-space: pre-line;
  font-family: serif;
  font-size: 14px;
  color: #3E2723;
  background-color: rgba(255, 248, 220, 0.95);
  border: 2px solid #8B7355;
  border-radius: 8px;
  box-shadow: 3px 3px 10px rgba(92, 64, 51, 0.3);
  pointer-events: none;
  z-index: 1000;
}
</style>
